import { NativeSelect } from "@mantine/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Campaign, CampaignBuild, CampaignChange } from "../api/types";
import { dateAndTime, orAbsent } from "../lib/format";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { FiltersPanel, Toolbar } from "../ui/Toolbar";
import { Reasons, reasonsOf } from "./Reasons";
import { RecordLink } from "./RecordLink";
import { Spinner } from "./Spinner";

/* What changed on a campaign's list at each update, and why.
 *
 * James, 2026-10-23: when a list goes from 30 properties to 29, a person can
 * look at a log and see that a property that was on it is gone because it no
 * longer matches the type, for example. The engine writes the log when it
 * builds a list (ingestion/campaign_changes.py, engine migration 287): each
 * property that joined or left against the list before, the cause in words,
 * and the reasons as the property's values, with what a value was before
 * when the engine recorded the change.
 *
 * Two tables: the updates, newest first, and what changed at the one chosen,
 * which is in the URL as ?build= so a link opens it. The engine writes every
 * word of a cause and a reason. */

const SELECT_BUILDS = "campaign,list_id,at,by_name,properties,previous_count,first,joined,left_count,reasons_recorded,note";
const SELECT_CHANGES = "id,campaign,list_id,change,cause,why,reasons,property_id,in_evie,label,city,state";

export function CampaignChanges({ token, c }: { token: string; c: Campaign }) {
  const name = c.name ?? "";
  const [search, setSearch] = useSearchParams();
  const params = useListParams("change", ["change"], false, "campaign_change", "cg.");
  const [filtersOpen, setFiltersOpen] = useState(Boolean(params.filters["change"]));
  const change = params.filters["change"] ?? "";

  const builds = useQuery({
    // The list's id in the key, so a new update shows as soon as the
    // campaign does, and Refresh on the page reaches it too.
    queryKey: ["campaign-builds", name, c.list_id],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("campaign_build").select(SELECT_BUILDS)
        .eq("campaign", name).order("at", { ascending: false }).limit(200);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as CampaignBuild[];
    },
  });
  const all = builds.data ?? [];
  // The update asked for, else the newest that changed anything, else the newest.
  const chosen = all.find((b) => b.list_id === search.get("build"))
    ?? all.find((b) => !b.first && (b.joined ?? 0) + (b.left_count ?? 0) > 0)
    ?? all[0] ?? null;
  const choose = (b: CampaignBuild) => setSearch((p) => {
    const next = new URLSearchParams(p);
    next.set("build", b.list_id ?? "");
    for (const k of [...next.keys()]) if (k.startsWith("cg.")) next.delete(k);
    return next;
  });

  const changes = useQuery({
    queryKey: ["campaign-changes", name, chosen?.list_id, params.page, params.sorts, params.filters, params.search],
    enabled: Boolean(chosen && !chosen.first),
    // The rows stay while a filter, a sort or a page loads.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      let q = apiClient(token).from("campaign_change").select(SELECT_CHANGES, { count: "exact" })
        .eq("campaign", name).eq("list_id", chosen?.list_id ?? "");
      if (change) q = q.eq("change", change);
      if (params.search.trim()) q = q.ilike("label", `*${params.search.trim()}*`);
      for (const s of params.sorts) q = q.order(s.column, { ascending: s.ascending, nullsFirst: false });
      const { data, error, count } = await q.order("label", { ascending: true })
        .order("property_id", { ascending: true }).range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as CampaignChange[], total: count ?? null };
    },
  });

  if (builds.isLoading) return <section className="rp-tabbody"><p className="muted"><Spinner />Loading.</p></section>;
  if (builds.isError) {
    return (
      <section className="rp-tabbody">
        <p className="refusal" role="alert">
          {(builds.error as Error).message} <Button size="compact-sm" onClick={() => builds.refetch()}>Try again</Button>
        </p>
      </section>
    );
  }
  if (!all.length) {
    return (
      <section className="rp-tabbody" aria-label="Changes">
        <p className="muted">No list yet. Each update of the list shows here, with the properties that joined and left and why.</p>
      </section>
    );
  }

  const count = (v: number | null | undefined) => (v ? v.toLocaleString() : <span className="muted">0</span>);
  const buildColumns: ColumnSpec<CampaignBuild>[] = [
    { id: "when", header: "Update", role: "leading", fields: [], title: true, narrowLabel: null,
      // A button as well as the row, so a keyboard and a screen reader can
      // choose an update too.
      cell: (b) => (
        <span className="cg-when">
          <button type="button" className="link" aria-current={b.list_id === chosen?.list_id ? "true" : undefined}
                  onClick={() => choose(b)}>{dateAndTime(b.at)}</button>
          {b.first ? <span className="muted cg-tag">First list</span>
            : !b.reasons_recorded ? <span className="muted cg-tag">No reasons recorded</span> : null}
        </span>
      ) },
    { id: "by", header: "By", role: "secondary", fields: [], narrowLabel: null, cell: (b) => orAbsent(b.by_name) },
    { id: "properties", header: "Properties", role: "secondary", fields: [], align: "num", narrowLabel: null,
      cell: (b) => b.first || b.previous_count === null || b.previous_count === undefined
        ? (b.properties ?? 0).toLocaleString()
        : `${b.previous_count.toLocaleString()} to ${(b.properties ?? 0).toLocaleString()}` },
    { id: "joined", header: "Joined", role: "secondary", fields: [], align: "num", narrowLabel: "joined",
      cell: (b) => (b.first ? "" : count(b.joined)) },
    { id: "left", header: "Left", role: "secondary", fields: [], align: "num", narrowLabel: "left",
      cell: (b) => (b.first ? "" : count(b.left_count)) },
  ];

  const changeColumns: ColumnSpec<CampaignChange>[] = [
    { id: "change", header: "Change", role: "leading", fields: [], narrowLabel: null, sort: "change",
      cell: (r) => (r.change === "joined" ? <Badge tone="ok">Joined</Badge> : <Badge tone="quiet">Left</Badge>) },
    { id: "property", header: "Property", role: "leading", fields: [], title: true, narrowLabel: null, sort: "label",
      // A property no longer in EVIE has no record to open, and says so in
      // words as well as in the muted color.
      cell: (r) => (r.in_evie ? <RecordLink to="properties" id={r.property_id} label={r.label} />
        : <span>{r.label}<span className="muted"> · no longer in EVIE</span></span>) },
    { id: "city", header: "City", role: "secondary", fields: [], narrowLabel: null, sort: "city", cell: (r) => orAbsent(r.city) },
    { id: "state", header: "State", role: "secondary", fields: [], narrowLabel: null, sort: "state", cell: (r) => orAbsent(r.state) },
    { id: "why", header: "Why", role: "leading", fields: [], narrowLabel: null, sort: "cause",
      cell: (r) => (
        <span className="cg-why">
          <span className="cg-cause">{r.why}</span>
          <Reasons reasons={reasonsOf(r.reasons)} />
        </span>
      ) },
  ];

  const chosenSays = !chosen ? null
    : chosen.first ? `The campaign's first list, with ${(chosen.properties ?? 0).toLocaleString()} ${chosen.properties === 1 ? "property" : "properties"}. Each update after it shows what joined and left.`
    : (chosen.joined ?? 0) + (chosen.left_count ?? 0) === 0 ? "No property joined or left at this update."
    : `${(chosen.previous_count ?? 0).toLocaleString()} to ${(chosen.properties ?? 0).toLocaleString()} properties: `
      + `${(chosen.joined ?? 0).toLocaleString()} joined and ${(chosen.left_count ?? 0).toLocaleString()} left.`
      + (chosen.by_name ? ` Updated by ${chosen.by_name}.` : "");
  const rows = changes.data?.rows ?? [];
  const total = changes.data?.total ?? null;
  const lastPage = total === null ? params.page : Math.max(1, Math.ceil(total / PAGE_SIZE));
  const chips = change ? [{ label: `Change: ${change === "joined" ? "Joined" : "Left"}`,
                            onRemove: () => params.set({ filters: {}, page: 1 }) }] : [];

  return (
    <section className="rp-tabbody cg" aria-label="Changes">
      <h3>Updates to the list</h3>
      {all.length >= 200 ? <p className="muted">The newest 200 updates.</p> : null}
      <DataTable columns={buildColumns} rows={all} params={params} rowKey={(b) => b.list_id ?? ""}
                 openId={chosen?.list_id ?? null} onOpen={choose} />

      {chosen ? (
        <>
          <h3>What changed at the update of {dateAndTime(chosen.at)}</h3>
          <p>{chosenSays}</p>
          {!chosen.first && !chosen.reasons_recorded && (chosen.joined ?? 0) + (chosen.left_count ?? 0) > 0 ? (
            <p className="muted">This update came before EVIE recorded why, so it shows which properties joined and left, and not why.</p>
          ) : null}
          {!chosen.first && (chosen.joined ?? 0) + (chosen.left_count ?? 0) > 0 ? (
            <>
              <Toolbar
                id="changes"
                search={{ label: "Search by property", value: params.search, onSubmit: (s) => params.set({ search: s, page: 1 }) }}
                filtersCount={chips.length}
                filtersOpen={filtersOpen}
                onToggleFilters={() => setFiltersOpen((v) => !v)}
                chips={chips}
                onClearAll={() => params.set({ search: "", filters: {}, page: 1 })}
              />
              <FiltersPanel id="changes" open={filtersOpen} onDone={() => setFiltersOpen(false)}>
                <div className="filters-own">
                  <NativeSelect label="Change" value={change}
                                data={[{ value: "", label: "Any" }, { value: "joined", label: "Joined" }, { value: "left", label: "Left" }]}
                                onChange={(e) => params.set({ filters: { change: e.currentTarget.value }, page: 1 })} />
                </div>
              </FiltersPanel>
              {changes.isLoading ? <p className="muted"><Spinner />Loading.</p>
                : changes.isError ? (
                  <p className="refusal" role="alert">
                    {(changes.error as Error).message} <Button size="compact-sm" onClick={() => changes.refetch()}>Try again</Button>
                  </p>
                ) : !rows.length ? (
                  <p className="muted">
                    No property matches. <Button kind="link" size="compact-sm" onClick={() => params.set({ search: "", filters: {}, page: 1 })}>Clear</Button>
                  </p>
                ) : (
                  <DataTable columns={changeColumns} rows={rows} params={params}
                             rowKey={(r) => r.id ?? `${r.property_id}-${r.change}`} />
                )}
              {lastPage > 1 ? (
                <div className="bar">
                  <Button onClick={() => params.set({ page: params.page - 1 })} disabled={params.page <= 1}>Previous</Button>
                  <span className="muted">Page {params.page.toLocaleString()} of {lastPage.toLocaleString()}</span>
                  <Button onClick={() => params.set({ page: params.page + 1 })} disabled={params.page >= lastPage}>Next</Button>
                </div>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
